import { type NonObject, type NonSdkClass } from 'utils/sdk_types';

/** The keys of every member of the union `T`, rather than only those all members share. */
export type AnyMemberKey<T> = T extends unknown ? keyof T : never;

/** The type of `K` in the members of the union `T` that have it. */
export type AnyMemberField<T, K extends PropertyKey> = T extends unknown
    ? K extends keyof T
        ? T[K]
        : never
    : never;

/** An SDK class, as `instanceof` takes it. */
export type SdkClass = abstract new (...args: never[]) => object;

type InstanceOf<C extends SdkClass> = C extends abstract new (
    ...args: never[]
) => infer I
    ? I
    : never;

type Renames = Readonly<Record<string, string | undefined>>;

/**
 * A declaration that an SDK class is a projection of an entity (e.g. an Alert's
 * embedded Issue): its objects are entities of `kind`, after their fields are
 * renamed to the entity's field names. See `projection`.
 */
export type Projection<
    Kind extends string = string,
    C extends SdkClass = SdkClass,
    R extends Renames = Renames,
> = Readonly<{
    kind: Kind;
    sdkClass: C;
    /** field renames from the SDK object to the entity's field names */
    renames: R;
}>;

/**
 * Declares `sdkClass` as a projection of entities of `kind`, with `renames` from
 * its field names to the entity's.
 */
export function projection<
    const Kind extends string,
    C extends SdkClass,
    const R extends Readonly<Partial<Record<keyof InstanceOf<C> & string, string>>>,
>(kind: Kind, sdkClass: C, renames: R): Projection<Kind, C, R> {
    return { kind, sdkClass, renames };
}

type Renamed<T, R> = {
    [K in keyof T as K extends keyof R ? R[K] & string : K]: T[K];
};

/** The projections among `P` that `T` is an instance of. */
type MatchingProjection<T, P extends Projection> = P extends Projection
    ? T extends InstanceOf<P['sdkClass']>
        ? P
        : never
    : never;

type PlainFields<T, P extends Projection> = NonSdkClass<{
    [K in keyof T]: NormalizedWith<T[K], P>;
}>;

/**
 * SDK data as normalized: plain objects at every depth, with instances of the
 * projections `P` renamed to their entity's field names.
 */
export type NormalizedWith<T, P extends Projection> = T extends unknown
    ? [MatchingProjection<T, P>] extends [never]
        ? T extends NonObject
            ? T
            : T extends Array<infer U>
              ? NormalizedWith<U, P>[]
              : T extends ReadonlyArray<infer U>
                ? readonly NormalizedWith<U, P>[]
                : PlainFields<T, P>
        : Renamed<PlainFields<T, P>, MatchingProjection<T, P>['renames']>
    : never;
